import type { IPublicClientApplication } from '@azure/msal-browser';

/**
 * MSAL account APIs (`getAllAccounts`, `getActiveAccount`) reflect **local browser cache only**,
 * not whether Azure AD still has an SSO session. An empty cache means no signed-in user **in this
 * app’s persisted MSAL state**; IdP cookies may still allow silent or interactive sign-in (see MSAL FAQ).
 */

/**
 * If one or more accounts exist but none is active, sets the active account (first entry).
 * MSAL recommends an active account for `acquireTokenSilent`; multi-account UIs may override selection.
 */
export function ensureMsalActiveAccountIfNeeded(
  instance: IPublicClientApplication | null | undefined,
): void {
  if (!instance) return;
  if (instance.getActiveAccount() != null) return;
  const accounts = instance.getAllAccounts();
  const first = accounts[0];
  if (first === undefined) return;
  instance.setActiveAccount(first);
}

/**
 * True when MSAL has **no** cached account entries after attempting {@link ensureMsalActiveAccountIfNeeded}.
 * Distinct from “no active account” alone: multi-account cache with no active selection is repaired first.
 */
export function hasEmptyMsalAccountCache(
  instance: IPublicClientApplication | null | undefined,
): boolean {
  if (!instance) return false;
  ensureMsalActiveAccountIfNeeded(instance);
  return instance.getAllAccounts().length === 0;
}
